import React, { useMemo, useState, useRef, useEffect } from "react";
import { get, isEqual } from "lodash";
import { LanguageCode, PriceTools } from "@al/base-lib";
import { withPage, WithReduxHocPropsType, WithI18nHocPropsType, WithPageHocPropsType } from "@al/next/al-ex";
import { createRoot } from "@al/aku-lc-renderer";
import { IRequestConfig, EventTrackingType } from "@al/aku-lc-core";
import { registerActionBuilder, ActionHTTPInputs } from "@al/aku-lc-schema";
import BridgeV2 from "@akulaku/web-bridge";

import performanceReport from "~/common/performanceReport";
import useEvent from "~/components/hooks/useEvent";
import { tracker } from "~/common/tracker";
import "~/common/utils/IntersectionObserver";
import withPerfCollect from "~/components/hoc/withPerfCollect";
import { getIO } from "~/common/io";
import goBack from "~/common/utils/goBack";
import Loading from "~/components/ui/Loading";
import Toast from "~/components/ui/Toast";
import NetworkError from "~/components/ui/NetworkError";
import PageStatusComp, { StatusType } from "~/components/ui/PageStatus";
import HeadTitle from "~/components/ui/HeadTitle";
import { ChannelType } from "~/components/basic/Share/types";

import i18ns from "./i18n";
import prefetchHelper from "./redux/prefetch-helper";
import resolveParams from "./utils/resolveParams";
import EventBusIns from "./utils/EventBus";
import SharePanel, { SharePanelProps } from "./components/SharePanel";
import Page from "./components/Page";
import Wrapper from "./components/Wrapper";
import ShareQrCode from "./components/ShareQrCode";
import { PageInfo, JsonData } from "./model/pageInfo";
import PageStatus from "./dict/PageStatus";
import { RCType, getComponentByName, registryComponents } from "./utils/registryComponent";
import getRequestParams from "./utils/getRequestParams";
import styles from "./styles.module.scss";
import ShareAction, { ShareActionPayload } from "./actions/ShareAction";
import SubmitAction, { SubmitActionPayload } from "./actions/SubmitAction";
import LinkToAction, { LinkToActionPayload, linkTarget } from "./actions/LinkTo";
import SameModalAction, { SameModalActionPayload } from "./actions/SameModalAction";
import DidMountAction, { DidMountActionPayload } from "./actions/DidMountAction";
import DelayedShowModalAction, { DelayedShowModalActionPayload } from "./actions/DelayedShowModalAction";
import { SPEventMap } from "./utils/SATracker";
import { getCustomI18nInstance } from "./utils/transformI18nData";
import getPlatform from "./utils/getPlatform";
import { formatImageUrl } from "./utils/formatImageUrl";
import SecurityVerification from "./components/SecurityVerification";

export interface Props extends WithReduxHocPropsType, WithI18nHocPropsType, WithPageHocPropsType {
  fetchPageInfo: any; // prefetch api
  asyncGetPageInfo: any; // prefetch api
}

const Main: React.FC<Props> = (props) => {
  const [sharePanelProps, setSharePanelProps] = useState<SharePanelProps>({
    visible: false,
  });
  const shareActionIns = useRef<ShareAction>();
  const { ctx, fetchPageInfo, t } = props;
  const pageData: PageInfo | null = get(fetchPageInfo, "value.data");
  const jsonData = pageData?.jsonData;
  // 自定义 header(部分参数从 bridge 获取)
  const customHeader = useRef<Record<string, string | number>>({});
  // 请求换成
  const requestCache = useRef<{ [key in string]: { config: IRequestConfig; data: any; isPromise?: boolean } }>({});
  const { json, i18nJson, components, componentUrls, status, needLogin, titleBarConfig, qrCodeConfig } = useMemo(() => {
    // 加载中
    if (!fetchPageInfo.isReady || !process.browser) {
      return {
        status: PageStatus.Loading,
      };
    }

    // 加载完成，但接口数据没返回数据
    if (!pageData || !jsonData) {
      return {
        status: PageStatus.Nodata,
      };
    }

    if (pageData.activityId) {
      // 活动未开始
      if (Number(pageData.startTime) > Date.now()) {
        return {
          status: PageStatus.ActivityNotStart,
        };
      }
      // 活动已结束
      if (Number(pageData.endTime) < Date.now()) {
        return {
          status: PageStatus.ActivityFinished,
        };
      }
    }

    try {
      const data: JsonData = JSON.parse(jsonData);
      const componentUrls = data.componentUrls || [];
      registryComponents(componentUrls);
      const componentMap: Record<string, RCType> = {};
      componentUrls.forEach((item) => {
        componentMap[item.name] = getComponentByName(item.name);
      });
      const shareConfigs = get(data.json, "schema.actions.shareAction.inputs", {});
      return {
        json: data.json,
        i18nJson: data.i18nJson,
        components: componentMap,
        componentUrls,
        status: PageStatus.Complete,
        needLogin: data.needLogin,
        titleBarConfig: data.titleBarConfig,
        privacyProtocolConfig: {
          privacyProtocolEnable: data.privacyProtocolEnable ?? false,
          privacyProtocolFileUrl: data.privacyProtocolFileUrl,
        },
        qrCodeConfig: {
          sharePanelQrCodeBg: shareConfigs.sharePanelQrCodeBg,
          sharePanelEnableQrCode: shareConfigs.sharePanelEnableQrCode,
        },
      };
    } catch (error) {
      return {
        status: PageStatus.Error,
      };
    }
  }, [jsonData]);

  useEffect(() => {
    const { activityId, activityRuleId, id } = ctx.query;
    tracker.setPageConfig({
      sa: {
        activityPlatform: "growth",
        page_id: `growth${id}`,
        page_name: pageData?.name ?? "",
        activityID: activityId ?? 0,
        activityRuleID: activityRuleId ?? 0,
      },
      behavior: {
        v4: {
          sn: 202172,
          sp: {
            /* eslint-disable */
            Aku_pageName: pageData?.name ?? "",
            Aku_activityID: activityId,
            Aku_activityRuleID: activityRuleId,
            Aku_activityPlatform: "growth",
            Aku_growthPageId: id,
            /* eslint-enable */
          },
        },
      },
    });
    tracker.enter({ behavior: { v4: {} } });
    tracker.activityEnter({
      sa: {
        page_view_type: "Enter",
      },
    });
    return (): void => {
      tracker.activityEnter({
        sa: {
          page_view_type: "Leave",
        },
      });
      tracker.leave({ behavior: { v4: {} } });
    };
  }, [pageData]);

  useEffect(() => {
    if (process.browser) {
      getPlatform().then((res) => {
        customHeader.current = { "device-type": res.deviceType };
        // 2023.3.1 appVersion 暂时不传，传了会走接口签名校验. web也暂未统一接入签名校验.
        // if (res.appVersion && res.appVersion > -1) {
        //   customHeader.current["app-version"] = res.appVersion;
        // }
      });
      const { id } = ctx.query;
      performanceReport({ traceName: `gp_main_${id}` });
    }
  }, []);

  useEffect(() => {
    if (titleBarConfig) {
      titleBarConfig.text &&
        BridgeV2.setWebRightTextAndAction({ text: translate(titleBarConfig.text) }, () => {
          // 点击回调成功且有链接，且未开启登录拦截
          if (titleBarConfig.link && handleBeforeExecuteAction() !== false) {
            linkTarget(titleBarConfig.link);
          }
        });
    }
  }, [titleBarConfig]);

  const handleChangeSharePanelProps = useEvent((params: SharePanelProps): void => {
    setSharePanelProps({
      ...sharePanelProps,
      ...params,
    });
  });

  const handleBeforeExecuteAction = useEvent((): boolean | void => {
    // 需要登录的时候
    if (needLogin && !ctx.store.getState().sign.isSign) {
      BridgeV2.goLogin("", () => {
        window.location.reload();
      });
      return false;
    }
  });

  const clickBeforeAgreePrivacyModal = useEvent((): boolean | void => {
    // 是否需要显示隐私弹窗
    return EventBusIns.emit("agreeAgreement")?.[0];
  });

  const languageCode = ctx.locale.languageCode as LanguageCode;

  const i18nInstance = useMemo(() => {
    return i18nJson ? getCustomI18nInstance(i18nJson, languageCode) : undefined;
  }, [i18nJson, languageCode]);

  // 翻译方法
  const translate = (key: string, options?: Record<string, any>): string => {
    // 格式校验，图片或者其他链接不翻译，处理
    if (!key || typeof key !== "string" || key?.startsWith("http://") || key?.startsWith("https://")) {
      return key;
    }
    const result = i18nInstance?.t(key, options);
    const resultKey = key ? String(key)?.replace("global:", "") : "";
    return (result === resultKey ? t(key, options) || key : result) || key;
  };

  // 请求方法
  const fetcher = useEvent(
    async (config: IRequestConfig): Promise<any> => {
      if (!config || !config.url) {
        return null;
      }
      const currentCache = requestCache.current[config.url];
      // noCache 请求不做缓存
      // originData 返回完整请求返回值
      const { noCache, originData, ...otherHeaders } = config.headers || {};
      // 读取缓存数据
      if (currentCache && isEqual(currentCache.config.data, config.data) && !noCache) {
        // 如果是promise
        if (currentCache.isPromise) {
          const { data } = await currentCache.data;
          return data;
        }
        return currentCache.data;
      }

      // 请求
      const method = (config.method && ["get", "GET"].includes(config.method) ? "get" : "post") as "get" | "post";
      const requestParamsSet = config.data || {};

      // 计算取值，接口中定义的如果本身未有值出入就去 query 取一遍。
      const searchParams = new URLSearchParams(location.search);
      const resultParams = resolveParams(requestParamsSet, searchParams);

      const requestData = {
        ...resultParams,
        activityId: requestParamsSet["activityId"] || ctx.query.activityId || undefined,
        activityRuleId: requestParamsSet["activityRuleId"] || ctx.query.activityRuleId || undefined,
        uid: getRequestParams({ requestParamsSet, query: ctx.query, key: "uid", queryKey: "code" }),
      };

      const promiseRequest = (getIO() as any)[method](config.url, requestData, {
        ...config,
        headers: {
          ...otherHeaders,
          ...customHeader.current,
          "device-id": ctx.deviceId,
          // "country-id": "1",
          // "language-id": "123",
        },
      });
      // 先加入promise缓存，防止同时发起请求
      requestCache.current[config.url] = {
        config,
        data: promiseRequest,
        isPromise: true,
      };
      const res = await promiseRequest;
      // 请求完成后改变promise状态
      requestCache.current[config.url] = {
        config,
        data: res.data,
      };

      // res.data 如果是 undefined 就会一直不更新组件，所以这里更新成 null 来触发组件更新。
      return (originData ? res : res.data) ?? null;
    }
  );

  const content = useMemo(() => {
    if (!json) {
      return null;
    }

    // 自定义上报事件
    const trackingReporter = (eventType: EventTrackingType, data?: any): void => {
      if (data && data.sa) {
        const {
          element_name: elementName,
          componentType,
          componentName,
          componentId,
          componentKey,
          links,
          channel,
          extra,
        } = data.sa;
        const componentID = componentId ?? componentUrls?.find((item) => item.name === componentName)?.id ?? 0;
        const commonData: Record<string, unknown> = {
          element_id: `AC_${componentID}`,
          element_name: elementName,
          componentID,
          componentType,
          componentKey: componentKey ?? "",
          channel: channel ?? "",
          links: links ?? "",
        };
        // 附加内容存储
        extra && (commonData.extra = JSON.stringify(extra));
        // input 事件要存储值
        if (eventType === "input" && data.__componentValue) {
          commonData.word = data.__componentValue;
          commonData.input_value = data.__componentValue;
        }
        // 自定义上报事件类型
        SPEventMap[eventType] && SPEventMap[eventType]?.(commonData);
        // tracker[eventType]?.({ sa: { element_name: elementName, componentType, ...commonData } });
      }
    };

    // 注册自定义 Action
    registerActionBuilder(
      ({
        // schema 内声明的 action 内容
        action,
        // action 的 key
        key,
        // // createRoot 时传入的参数
        // rootOptions,
        // // 执行 action 的表达式方法
        // evaluate,
        // // 转换 action 的 runAfter 字段
        transformActionRunAfter,
      }) => {
        // 返回对应 type 的 Action 实例，只处理自己自定义的 type
        let instance = null;
        switch (action.type) {
          case "shareAction":
            shareActionIns.current = instance = new ShareAction({
              path: key,
              type: "shareAction" as any,
              payload: {
                ctx: ctx,
                ...action.inputs,
                onChangeSharePanelProps: handleChangeSharePanelProps,
                onShareChanelClick: trackingReporter,
              } as ShareActionPayload,
              onBeforeExecute: handleBeforeExecuteAction,
            });
            break;
          case "submitAction":
            instance = new SubmitAction({
              path: key,
              type: "submitAction" as any,
              payload: {
                ...(action.inputs as ActionHTTPInputs),
                fetcher: fetcher,
              } as SubmitActionPayload,
            });
            break;
          case "linkToAction":
            instance = new LinkToAction({
              path: key,
              type: "linkToAction" as any,
              payload: action.inputs as LinkToActionPayload,
              onBeforeExecute: handleBeforeExecuteAction,
            });
            break;
          // 固定弹窗
          case "sameModalAction":
            instance = new SameModalAction({
              path: key,
              type: "sameModalAction" as any,
              payload: action.inputs as SameModalActionPayload,
            });
            break;
          // 接口弹窗
          case "requestModalAction":
            instance = new DidMountAction({
              path: key,
              type: "requestModalAction" as any,
              payload: {
                ...(action.inputs as ActionHTTPInputs),
                fetcher: fetcher,
              } as DidMountActionPayload,
            });
            break;

          case "didMountAction":
            if ((action.inputs as ActionHTTPInputs)?.data?._gp_process === "OpenModal") {
              instance = new DelayedShowModalAction({
                path: key,
                type: "delayedShowModalAction" as any,
                payload: (action.inputs as ActionHTTPInputs)?.data as DelayedShowModalActionPayload,
              });
            } else {
              instance = new DidMountAction({
                path: key,
                type: "didMountAction" as any,
                payload: {
                  ...(action.inputs as ActionHTTPInputs),
                  fetcher: fetcher,
                } as DidMountActionPayload,
              });
            }

            break;
          default:
            break;
        }

        if (instance) {
          action.runAfter && (instance.runAfter = transformActionRunAfter(action.runAfter));
          return instance;
        }
      }
    );

    // console.log("json", json);
    const Root = createRoot({
      fetcher,
      translate,
      eventTracking: {
        report: trackingReporter,
      },
      notify: {
        success: (text: string, duration?: number): void => {
          Toast.success(text, duration);
        },
        fail: (text: string, duration?: number): void => {
          Toast.fail(text, duration);
        },
      },
      widgets: {
        Page,
        Wrapper,
        ...components,
      },
      extraConfig: {
        countryCode: ctx.locale.countryCode,
        languageCode: ctx.locale.languageCode,
        formatPrice: (type: "price" | "shortprice", val: string | number, needCurrency = false): string =>
          PriceTools.formatPrice(ctx.locale.countryCode, val, type, needCurrency),
        goBack,
        linkTo: linkTarget,
        isMobile: true,
        isPlatform: false,
        isBrowser: process.browser,
        isSign: ctx.store.getState().sign.isSign,
        query: ctx.router.query,
        EventBusIns,
        clickBeforeSign: handleBeforeExecuteAction,
        clickBeforeAgreePrivacyModal: clickBeforeAgreePrivacyModal,
        formatImageUrl,
      },
    });
    return <Root schema={json} />;
  }, [json]);

  const getShareInfo = useEvent((channelId: ChannelType) => {
    return shareActionIns.current?.getShareInfo(channelId);
  });

  if (status === PageStatus.Loading) {
    return <Loading visible={!json} />;
  }

  if (status === PageStatus.Error) {
    // 显示网络异常
    return <NetworkError />;
  }

  if (status === PageStatus.ActivityNotStart) {
    // 活动未开始
    return <PageStatusComp status={StatusType.NoService} />;
  }

  if (status === PageStatus.ActivityFinished || status === PageStatus.Nodata) {
    // 活动已结束
    return <PageStatusComp status={StatusType.Finished} />;
  }

  // 页面标题
  const pageTitle = translate(get(json?.schema, "interface.props.title") || "");

  return (
    <div className={styles.container}>
      <HeadTitle title={pageTitle || ""} />
      <div className={styles.preview_wrapper}>{content}</div>
      <SharePanel {...sharePanelProps} />
      {/* <SharePanel {...sharePanelProps} /> */}
      {process.browser ? <SecurityVerification /> : null}
      {qrCodeConfig?.sharePanelEnableQrCode && (
        <ShareQrCode backImg={qrCodeConfig.sharePanelQrCodeBg} getShareInfo={getShareInfo} fetcher={fetcher} />
      )}
    </div>
  );
};

export default withPerfCollect({
  needNetResource: true,
  criticalAPI: prefetchHelper.asyncGetPageInfo.url,
})(
  withPage(({ withI18n }) => [
    {
      className: styles.growth_publish_main,
      prefetchApi: { fetchPageInfo: prefetchHelper.asyncGetPageInfo },
    },
    withI18n("main", i18ns),
  ])(Main)
);
